import { Injectable, inject, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { TranscriptionServiceProxy } from "@api/core";
import { StopTranscriptionRequest } from "@api/core.models";
import { RecordingStateService } from "./recording-state.service";
import { AudioCaptureService } from "./audio-capture.service";

export interface RecordingEndResult {
    ended: boolean;
    transcriptionId: number | null;
}

@Injectable({ providedIn: "root" })
export class RecordingEndService {
    private readonly _state = inject(RecordingStateService);
    private readonly _audioCapture = inject(AudioCaptureService);
    private readonly _proxy = inject(TranscriptionServiceProxy);

    readonly isEnding = signal(false);

    private _endConfirmationOpen = false;
    private _inFlight: Promise<RecordingEndResult> | null = null;

    claimEndConfirmation(): boolean {
        if (this._endConfirmationOpen || this.isEnding()) {
            return false;
        }
        this._endConfirmationOpen = true;
        return true;
    }

    releaseEndConfirmation(): void {
        this._endConfirmationOpen = false;
    }

    endAndTranscribe(): Promise<RecordingEndResult> {
        this._inFlight ??= this._stopAndQueueTranscription();
        return this._inFlight;
    }

    private async _stopAndQueueTranscription(): Promise<RecordingEndResult> {
        this.isEnding.set(true);
        try {
            return await this._stopUploadAndMarkProcessing();
        } finally {
            this.isEnding.set(false);
            this._endConfirmationOpen = false;
            this._inFlight = null;
        }
    }

    private async _stopUploadAndMarkProcessing(): Promise<RecordingEndResult> {
        const transcriptionId = this._state.recordingId();
        if (transcriptionId === null) {
            return { ended: true, transcriptionId: null };
        }

        try {
            await this._audioCapture.stop();
        } catch {
            // local stream cleanup failure is non-fatal
        }

        try {
            await firstValueFrom(this._proxy.stopUpload(new StopTranscriptionRequest({ transcriptionId })));
        } catch {
            return { ended: false, transcriptionId };
        }

        this._state.markProcessing(transcriptionId);
        this._state.stopRecording();
        return { ended: true, transcriptionId };
    }
}
